import { useState, useEffect, useRef, ChangeEvent } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import { getUserByToken } from "../core/_requests";
import { Link, useNavigate } from "react-router-dom";
import { toAbsoluteUrl } from "../../../../_metronic/helpers";
import { PasswordMeterComponent } from "../../../../_metronic/assets/ts/components";
import { useAuth } from "../core/Auth";
import { register } from "../../../api";

const initialValues = {
  username: "",
  email: "",
  website: "",
  organization_name: "",
  password: "",
  confirmpassword: "",
  acceptTerms: false
};

// List of personal email domains to block
const personalEmailDomains = [
  'gmail.com',
  'outlook.com',
  'hotmail.com',
  'yahoo.com',
  'aol.com',
  'icloud.com',
  'protonmail.com',
  'zoho.com',
  'mail.com',
  'yandex.com',
  'gmx.com',
  'live.com',
  'msn.com',
];

const registrationSchema = Yup.object().shape({
  username: Yup.string()
    .test('is-lowercase', 'Username must be lowercase', value => value != null && value === value.toLowerCase())
    .matches(/^[a-z]+$/, "Username must not contain numbers or special characters")
    .min(3, "Minimum 3 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Username is required"),
  email: Yup.string()
    .email("Wrong email format")
    .test('is-not-personal', 'Please use a work email (not a personal email like Gmail)', function(value) {
      if (!value) return true;
      const domain = value.split('@')[1];
      return !personalEmailDomains.includes(domain);
    })
    .min(3, "Minimum 3 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Email is required"),
  organization_name: Yup.string()
    .min(3, "Minimum 3 symbols")
    .max(20, "Maximum 50 symbols")
    .required("Organization Name is required"),
  website: Yup.string()
    .url('Invalid website URL format')
    .matches(
      /^(https?:\/\/)?(www\.)?([a-zA-Z0-9]+)\.[a-zA-Z]{2,}(\/\S*)?$/,
      'Invalid website format'
    )
    .required('Website is required'),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .max(20, "Password must not exceed 20 characters")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(/[@$!%*?&#]/, "Password must contain at least one special character")
    .required("Password is required"),
  confirmpassword: Yup.string()
    .min(3, "Minimum 3 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Password confirmation is required")
    .oneOf([Yup.ref("password")], "Password and Confirm Password didn't match"),
  captchaInput: Yup.string()
    .required("Please complete the captcha")
    .test('captcha-match', 'Incorrect captcha code', function(value) {
      return value === this.parent.captchaValue;
    }),
});

export function Registration() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { saveAuth, setCurrentUser } = useAuth();
  
  // Enhanced captcha states
  const [captchaText, setCaptchaText] = useState('');
  const [userCaptcha, setUserCaptcha] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  
  // Generate a random captcha code
  const generateCaptcha = () => {
    const characters = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let result = '';
    let display = '';
    
    for (let i = 0; i < 6; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
      display += characters[randomIndex] + ' ';
    }
    
    return { value: result, display: display.trim() };
  };
  
  // Regenerate captcha
  const refreshCaptcha = () => {
    const newCaptcha = generateCaptcha();
    setCaptchaText(newCaptcha.display);
    formik.setFieldValue('captchaValue', newCaptcha.value);
    setUserCaptcha('');
    setCaptchaError('');
  };
  
  // Handle captcha input change with proper type annotation
  const handleCaptchaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserCaptcha(e.target.value);
    formik.setFieldValue('captchaInput', e.target.value);
    setCaptchaError('');
  };

  // Initialize captcha on component mount
  useEffect(() => {
    const newCaptcha = generateCaptcha();
    setCaptchaText(newCaptcha.display);
    formik.setFieldValue('captchaValue', newCaptcha.value);
  }, []);

  const formik = useFormik({
    initialValues: {
      ...initialValues,
      captchaValue: '', // Hidden field to store the actual captcha value
      captchaInput: '', // Field to store user input for captcha
    },
    validationSchema: registrationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      // Validate captcha before submission
      if (values.captchaInput !== values.captchaValue) {
        setCaptchaError('Incorrect captcha code');
        return;
      }
      
      const { username, email, organization_name, website, password } = values;
      setLoading(true);
      try {
        register({ 
          "username": username, 
          "email": email, 
          "organization_name": organization_name, 
          "password": password, 
          "subscription": "free_trail", 
          "website": website, 
          "type": "superadmin" 
        }).then(async (res) => {
          alert("Your are successfully registered.")
          navigate('/auth/login')
          setLoading(false);
        }).catch((e) => {
          if (e.response) { // Check if error.response exists
            alert("Error in registering user");
            saveAuth(undefined);
            setStatus("Facing Server Issue. Try Again Later");
            setSubmitting(false);
            setLoading(false);
          } else {
            alert("Oops! Something went wrong. Please try again later.");
            saveAuth(undefined);
            setStatus("Facing Server Issue. Try Again Later");
            setSubmitting(false);
            setLoading(false);
          }
        });
      } catch (error) {
        console.error(error);
        saveAuth(undefined);
        setStatus("The registration details is incorrect");
        setSubmitting(false);
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    PasswordMeterComponent.bootstrap();
  }, []);

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[@$!%*?&#]/.test(password)) strength += 1;
    return strength;
  };
  
  const strength = getPasswordStrength(formik.values.password);
  
  return (
    <form
      className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework"
      noValidate
      id="kt_login_signup_form"
      onSubmit={formik.handleSubmit}
    >
      {/* begin::Heading */}
      <div className="text-center mb-11">
        {/* begin::Title */}
        <h1 className="text-dark fw-bolder mb-3">Sign Up</h1>
        {/* end::Title */}

        <div className="text-gray-500 fw-semibold fs-6">Organization Registration</div>
      </div>
      {/* end::Heading */}

      {formik.status && (
        <div className="mb-lg-15 alert alert-danger">
          <div className="alert-text font-weight-bold">{formik.status}</div>
        </div>
      )}

      {/* begin::Form group username */}
      <div className="fv-row mb-8">
        <label className="form-label fw-bolder text-dark fs-6">
          Username
        </label>
        <input
          placeholder="Username"
          type="text"
          autoComplete="off"
          {...formik.getFieldProps("username")}
          className={clsx(
            "form-control bg-transparent",
            {
              "is-invalid":
                formik.touched.username && formik.errors.username
            },
            {
              "is-valid":
                formik.touched.username && !formik.errors.username
            }
          )}
        />
        {formik.touched.username && formik.errors.username && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">
              <span role="alert">{formik.errors.username}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group Email */}
      <div className="fv-row mb-8">
        <label className="form-label fw-bolder text-dark fs-6">Email</label>
        <input
          placeholder="Email"
          type="email"
          autoComplete="off"
          {...formik.getFieldProps("email")}
          className={clsx(
            "form-control bg-transparent",
            { "is-invalid": formik.touched.email && formik.errors.email },
            {
              "is-valid": formik.touched.email && !formik.errors.email
            }
          )}
        />
        {formik.touched.email && formik.errors.email && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">
              <span role="alert">{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      <div className="fv-row mb-8">
        {/* begin::Form group organization_name */}
        <label className="form-label fw-bolder text-dark fs-6">
          Organization Name
        </label>
        <input
          placeholder="Organization Name"
          type="text"
          autoComplete="off"
          {...formik.getFieldProps("organization_name")}
          className={clsx(
            "form-control bg-transparent",
            {
              "is-invalid":
                formik.touched.organization_name && formik.errors.organization_name
            },
            {
              "is-valid":
                formik.touched.organization_name && !formik.errors.organization_name
            }
          )}
        />
        {formik.touched.organization_name && formik.errors.organization_name && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">
              <span role="alert">{formik.errors.organization_name}</span>
            </div>
          </div>
        )}
        {/* end::Form group */}
      </div>

      {/* begin::Form group Website*/}
      <div className="fv-row mb-8">
        <label className="form-label fw-bolder text-dark fs-6">
          Website
        </label>
        <input
          placeholder="Website Link"
          type="text"
          autoComplete="off"
          {...formik.getFieldProps("website")}
          className={clsx(
            "form-control bg-transparent",
            {
              "is-invalid":
                formik.touched.website && formik.errors.website
            },
            {
              "is-valid":
                formik.touched.website && !formik.errors.website
            }
          )}
        />
        {formik.touched.website && formik.errors.website && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">
              <span role="alert">{formik.errors.website}</span>
            </div>
          </div>
        )}
      </div>

      {/* begin::Form group Password */}
      <div className="fv-row mb-8" data-kt-password-meter="true">
        <div className="mb-1">
          <label className="form-label fw-bolder text-dark fs-6">
            Password
          </label>
          <div className="position-relative mb-3">
            <input
              type="password"
              placeholder="Password"
              autoComplete="off"
              {...formik.getFieldProps("password")}
              className={clsx(
                "form-control bg-transparent",
                {
                  "is-invalid":
                    formik.touched.password && formik.errors.password
                },
                {
                  "is-valid": formik.touched.password && !formik.errors.password
                }
              )}
            />
            {formik.touched.password && formik.errors.password && (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">
                  <span role="alert">{formik.errors.password}</span>
                </div>
              </div>
            )}
          </div>
          {/* begin::Meter */}
          <div className="d-flex align-items-center mb-3" data-kt-password-meter-control="highlight">
            <div className={`flex-grow-1 rounded h-5px me-2 ${strength > 0 ? 'bg-success' : 'bg-secondary'}`}></div>
            <div className={`flex-grow-1 rounded h-5px me-2 ${strength > 1 ? 'bg-success' : 'bg-secondary'}`}></div>
            <div className={`flex-grow-1 rounded h-5px me-2 ${strength > 2 ? 'bg-success' : 'bg-secondary'}`}></div>
            <div className={`flex-grow-1 rounded h-5px ${strength > 3 ? 'bg-success' : 'bg-secondary'}`}></div>
          </div>
          {/* end::Meter */}
        </div>
        <div className="text-muted">
          Use 8 or more characters with a mix of letters, numbers & symbols.
        </div>
      </div>
      {/* end::Form group */}

      {/* begin::Form group Confirm password */}
      <div className="fv-row mb-5">
        <label className="form-label fw-bolder text-dark fs-6">
          Confirm Password
        </label>
        <input
          type="password"
          placeholder="Password confirmation"
          autoComplete="off"
          {...formik.getFieldProps("confirmpassword")}
          className={clsx(
            "form-control bg-transparent",
            {
              "is-invalid":
                formik.touched.confirmpassword && formik.errors.confirmpassword
            },
            {
              "is-valid":
                formik.touched.confirmpassword && !formik.errors.confirmpassword
            }
          )}
        />
        {formik.touched.confirmpassword && formik.errors.confirmpassword && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">
              <span role="alert">{formik.errors.confirmpassword}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Enhanced Captcha Section */}
      <div className="fv-row mb-8">
        <label className="form-label fw-bolder text-dark fs-6">
          Captcha Verification
        </label>
        <div className="d-flex mb-2">
          <div 
            className="border p-3 bg-light me-3 d-inline-block" 
            style={{ 
              fontSize: '1.2rem', 
              fontFamily: 'monospace',
              letterSpacing: '3px',
              fontWeight: 'bold',
              color: '#000',
              textDecoration: 'line-through',
              background: 'linear-gradient(45deg, #f1f1f1, #e1e1e1)',
              position: 'relative',
              overflow: 'hidden',
              minWidth: '120px',
              textAlign: 'center'
          }}
          >
            {captchaText}
          </div>
          <button 
            type="button" 
            onClick={refreshCaptcha}
            className="btn btn-sm btn-light-primary"
          >
            <i className="bi bi-arrow-repeat"></i>
            <span className="ms-1 d-none d-sm-inline">Refresh</span>
          </button>
        </div>
        <input
          type="text"
          placeholder="Enter the code shown above"
          autoComplete="off"
          value={userCaptcha}
          onChange={handleCaptchaChange}
          className={clsx(
            "form-control bg-transparent",
            {
              "is-invalid": (formik.touched.captchaInput && formik.errors.captchaInput) || captchaError
            },
            {
              "is-valid": formik.touched.captchaInput && !formik.errors.captchaInput && !captchaError && userCaptcha
            }
          )}
        />
        <input 
          type="hidden" 
          {...formik.getFieldProps("captchaValue")} 
        />
        {captchaError && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">
              <span role="alert">{captchaError}</span>
            </div>
          </div>
        )}
        {formik.touched.captchaInput && formik.errors.captchaInput && !captchaError && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">
              <span role="alert">{formik.errors.captchaInput}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Enhanced Captcha Section */}

      {/* begin::Form group */}
      <div className="text-center">
        <button
          type="submit"
          id="kt_sign_up_submit"
          className="btn btn-lg btn-success w-100 mb-5"
          disabled={
            formik.isSubmitting || !formik.isValid || !userCaptcha
          }
        >
          {!loading && <span className="indicator-label">Submit</span>}
          {loading && (
            <span className="indicator-progress" style={{ display: "block" }}>
              Please wait...{" "}
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
        <Link to="/auth/login">
          <button
            type="button"
            id="kt_login_signup_form_cancel_button"
            className="btn btn-lg btn-light-primary w-100 mb-5"
          >
            Cancel
          </button>
        </Link>
      </div>
      {/* end::Form group */}
    </form>
  );
}